import React, { useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { PlugZap, CheckCircle2, XCircle } from "lucide-react";
import useInvoicingStore from "../../../../../store/invoicing";
import { Button } from "../../../../../components/ui/Button";
import { Input } from "../../../../../components/ui/Input";
import { Select } from "../../../../../components/ui/Select";
import { Field, FileDrop, InfoNote } from "./WizardControls";
import { apiErrorMessage } from "../../../../../utils/apiErrorMessage";
import {
  entity,
  envLabel,
  PREREQUISITES,
  IVA_OPTIONS,
  DOC_TYPE_OPTIONS,
  CAF_DOC_TYPES,
  EMIT_TRIGGER_OPTIONS,
  TAX_ID_LABEL,
  TAX_RATE_HINT,
} from "./fiscalCopy";
import {
  isValidTaxId,
  isEmail,
  TAX_ID_PLACEHOLDER,
} from "./fiscalValidation";

// Catálogo de pasos del wizard (orden y título). El componente concreto se
// resuelve en el orquestador. La secuencia real depende del país y el ambiente.
export const STEP_META = {
  prerequisites: { title: "Antes de empezar" },
  identity: { title: "Datos fiscales del emisor" },
  certificate: { title: "Certificado digital" },
  folios: { title: "Folios (CAF)" },
  pos: { title: "Punto de venta" },
  emission_rules: { title: "Reglas de emisión" },
  connection_test: { title: "Prueba de conexión" },
  certification: { title: "Certificación aprobada" },
  activation: { title: "Activación" },
};

// ── Layout compartido de un paso ──────────────────────────────────────────────
function StepLayout({ title, description, children }) {
  return (
    <div className="flex flex-col gap-4">
      <div>
        <h3 className="text-base font-semibold text-fg">{title}</h3>
        {description && <p className="text-sm text-fg-muted mt-0.5">{description}</p>}
      </div>
      {children}
    </div>
  );
}

function StepFooter({ onBack, backDisabled, primaryLabel, onPrimary, primaryLoading, primaryDisabled, secondary }) {
  const { t } = useTranslation();
  return (
    <div className="flex items-center justify-between gap-2 pt-2">
      <Button variant="ghost" onClick={onBack} disabled={backDisabled}>
        {t("Atrás")}
      </Button>
      <div className="flex items-center gap-2">
        {secondary}
        <Button variant="primary" onClick={onPrimary} loading={primaryLoading} disabled={primaryDisabled}>
          {primaryLabel}
        </Button>
      </div>
    </div>
  );
}

// ── Paso 1: prerrequisitos oficiales (declaración del operador) ────────────────
export function PrerequisitesStep({ country, goBack, goNext, isFirst }) {
  const { t } = useTranslation();
  const e = entity(country);
  const items = PREREQUISITES[country] ?? [];
  const required = items.filter((i) => i.required);
  const [checked, setChecked] = useState({});
  const allRequired = required.every((i) => checked[i.id]);

  const toggle = (id) => setChecked((p) => ({ ...p, [id]: !p[id] }));

  return (
    <StepLayout
      title={t("Antes de empezar")}
      description={t("Confirma que ya completaste los trámites que {{authName}} exige fuera de Beartrack para habilitarte.", { authName: e.authName })}
    >
      <InfoNote tone="info">
        <Trans
          i18nKey="Esto es una <strong>declaración tuya</strong>: Beartrack no puede verificar el estado de estos trámites ante {{authName}}. Si marcas algo que no tienes, la prueba de conexión fallará más adelante."
          values={{ authName: e.authName }}
          components={{ strong: <strong /> }}
        />
      </InfoNote>
      <ul className="flex flex-col gap-2.5">
        {items.map((item) => (
          <li key={item.id}>
            <label className="flex items-start gap-2.5 cursor-pointer">
              <input
                type="checkbox"
                checked={!!checked[item.id]}
                onChange={() => toggle(item.id)}
                className="mt-0.5 h-4 w-4 accent-brand-500"
              />
              <span className="text-sm text-fg leading-snug">
                {t(item.label)}
                {!item.required && <span className="text-fg-subtle">{t(" (solo producción)")}</span>}
              </span>
            </label>
          </li>
        ))}
      </ul>
      <StepFooter
        onBack={goBack}
        backDisabled={isFirst}
        primaryLabel={t("Confirmo, continuar")}
        onPrimary={goNext}
        primaryDisabled={!allRequired}
      />
    </StepLayout>
  );
}

// ── Paso 2: identidad fiscal ───────────────────────────────────────────────────
export function IdentityStep({ config, country, environment, operatorName, goBack, goNext }) {
  const { t } = useTranslation();
  const saveIdentity = useInvoicingStore((s) => s.saveIdentity);
  const saving = useInvoicingStore((s) => s.saving);

  const [form, setForm] = useState(() => ({
    legalName: config?.legalName ?? operatorName ?? "",
    fantasyName: config?.fantasyName ?? "",
    taxId: config?.taxId ?? "",
    ivaCondition: config?.ivaCondition ?? "",
    economicActivity: config?.economicActivity ?? "",
    street: config?.address?.street ?? "",
    city: config?.address?.city ?? "",
    state: config?.address?.state ?? "",
    postalCode: config?.address?.postalCode ?? "",
    emissionEmail: config?.emissionEmail ?? "",
  }));
  const [err, setErr] = useState("");

  const set = (k) => (ev) => setForm((p) => ({ ...p, [k]: ev.target?.value ?? ev }));

  const taxIdOk = isValidTaxId(country, form.taxId);
  const emailOk = isEmail(form.emissionEmail);
  const canSave =
    form.legalName.trim().length >= 2 &&
    taxIdOk &&
    !!form.ivaCondition &&
    form.street.trim().length >= 3 &&
    form.city.trim().length >= 2 &&
    form.state.trim().length >= 2 &&
    emailOk;

  const submit = async () => {
    setErr("");
    try {
      await saveIdentity({
        country,
        environment,
        legalName: form.legalName.trim(),
        fantasyName: form.fantasyName.trim() || undefined,
        taxId: form.taxId.trim(),
        ivaCondition: form.ivaCondition,
        economicActivity: form.economicActivity.trim() || undefined,
        address: {
          street: form.street.trim(),
          city: form.city.trim(),
          state: form.state.trim(),
          postalCode: form.postalCode.trim() || undefined,
        },
        emissionEmail: form.emissionEmail.trim(),
      });
      goNext();
    } catch (ex) {
      setErr(apiErrorMessage(ex, t("No se pudo guardar.")));
    }
  };

  return (
    <StepLayout
      title={t("Datos fiscales del emisor")}
      description={t("Son los datos con los que firmarás los comprobantes. Deben coincidir con tu inscripción ante el ente.")}
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field label={t("Razón social")} required help={t("Tal como está inscrita ante el ente.")}>
          <Input value={form.legalName} onChange={set("legalName")} placeholder={t("Mi Empresa SpA")} />
        </Field>
        <Field label={t("Nombre de fantasía")} help={t("Opcional, si difiere de la razón social.")}>
          <Input value={form.fantasyName} onChange={set("fantasyName")} />
        </Field>
        <Field
          label={t(TAX_ID_LABEL[country])}
          required
          error={form.taxId && !taxIdOk ? t("{{taxIdLabel}} inválido (dígito verificador).", { taxIdLabel: TAX_ID_LABEL[country] }) : ""}
          help={t("Identificador tributario del emisor.")}
        >
          <Input
            value={form.taxId}
            onChange={set("taxId")}
            placeholder={TAX_ID_PLACEHOLDER[country]}
            invalid={!!form.taxId && !taxIdOk}
          />
        </Field>
        <Field
          label={t("Condición frente al IVA")}
          required
          help={t("Es tu condición tributaria, NO la forma societaria (SAS/SpA/SRL no van aquí).")}
        >
          <Select
            value={form.ivaCondition}
            onChange={set("ivaCondition")}
            placeholder={t("Selecciona…")}
            options={(IVA_OPTIONS[country] ?? []).map((o) => ({ ...o, label: t(o.label) }))}
          />
        </Field>
        <Field label={t("Giro / actividad económica")} help={t("Opcional.")}>
          <Input value={form.economicActivity} onChange={set("economicActivity")} />
        </Field>
        <Field label={t("Correo de emisión")} required help={t("Correo de contacto del emisor.")}>
          <Input
            type="email"
            value={form.emissionEmail}
            onChange={set("emissionEmail")}
            invalid={!!form.emissionEmail && !emailOk}
          />
        </Field>
      </div>

      <div className="text-xs font-semibold uppercase tracking-wide text-fg-subtle mt-1">
        {t("Dirección fiscal")}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field label={t("Calle y número")} required>
          <Input value={form.street} onChange={set("street")} />
        </Field>
        <Field label={t(country === "AR" ? "Localidad" : "Comuna")} required>
          <Input value={form.city} onChange={set("city")} />
        </Field>
        <Field label={t(country === "AR" ? "Provincia" : "Región")} required>
          <Input value={form.state} onChange={set("state")} />
        </Field>
        <Field label={t("Código postal")} help={t("Opcional según el país.")}>
          <Input value={form.postalCode} onChange={set("postalCode")} />
        </Field>
      </div>

      {err && <InfoNote tone="warning">{err}</InfoNote>}
      <StepFooter
        onBack={goBack}
        primaryLabel={t("Guardar y continuar")}
        onPrimary={submit}
        primaryLoading={saving}
        primaryDisabled={!canSave}
      />
    </StepLayout>
  );
}

// ── Paso 3: certificado digital ────────────────────────────────────────────────
export function CertificateStep({ config, country, goBack, goNext }) {
  const { t } = useTranslation();
  const upload = useInvoicingStore((s) => s.uploadCertificate);
  const saving = useInvoicingStore((s) => s.saving);
  const [file, setFile] = useState(null);
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [replacing, setReplacing] = useState(false);

  const hasCert = !!config?.certificateSubject;
  const showForm = !hasCert || replacing;

  const submit = async () => {
    setErr("");
    try {
      await upload(file, password);
      setReplacing(false);
      setFile(null);
      setPassword("");
    } catch (ex) {
      setErr(apiErrorMessage(ex, t("No se pudo validar el certificado.")));
    }
  };

  return (
    <StepLayout
      title={t("Certificado digital")}
      description={t("Sube el certificado del emisor. Lo validamos (vigencia y titularidad) y lo guardamos cifrado.")}
    >
      {hasCert && !replacing && (
        <div className="rounded-lg border border-border bg-bg-muted px-3 py-3 flex items-start gap-2.5">
          <CheckCircle2 className="w-5 h-5 text-status-success mt-0.5 shrink-0" aria-hidden />
          <div className="text-sm">
            <div className="font-medium text-fg">{t("Certificado cargado")}</div>
            <div className="text-fg-muted break-all">{config.certificateSubject}</div>
            {config.certificateExpiresAt && (
              <div className="text-fg-muted">
                {t("Vence el {{date}}", { date: new Date(config.certificateExpiresAt).toLocaleDateString() })}
              </div>
            )}
          </div>
        </div>
      )}

      {showForm && (
        <>
          <FileDrop
            accept=".pfx,.p12,.pem,.crt"
            file={file}
            onSelect={(f) => {
              setFile(f);
              setErr("");
            }}
            hint={
              country === "AR"
                ? t("Certificado de homologación (WSASS) o de producción, según el ambiente.")
                : t("Certificado digital acreditado ante el SII, asociado a tu RUT.")
            }
          />
          <Field label={t("Contraseña del certificado")} help={t("Necesaria para abrir el .pfx. Se guarda cifrada y nunca se muestra.")}>
            <Input type="password" value={password} onChange={(ev) => setPassword(ev.target.value)} />
          </Field>
          {err && <InfoNote tone="warning">{err}</InfoNote>}
        </>
      )}

      <StepFooter
        onBack={goBack}
        primaryLabel={showForm ? t("Subir y validar") : t("Continuar")}
        onPrimary={showForm ? submit : goNext}
        primaryLoading={saving}
        primaryDisabled={showForm && !file}
        secondary={
          hasCert && !replacing ? (
            <Button variant="outline" onClick={() => setReplacing(true)}>
              {t("Reemplazar")}
            </Button>
          ) : null
        }
      />
    </StepLayout>
  );
}

// ── Paso 4a: folios CAF (Chile) ────────────────────────────────────────────────
export function FoliosStep({ config, goBack, goNext }) {
  const { t } = useTranslation();
  const upload = useInvoicingStore((s) => s.uploadCaf);
  const saving = useInvoicingStore((s) => s.saving);
  const [file, setFile] = useState(null);
  const [docTypeCode, setDocTypeCode] = useState("33");
  const [err, setErr] = useState("");
  const [warning, setWarning] = useState("");

  const cafs = config?.cafs ?? [];

  const submit = async () => {
    setErr("");
    setWarning("");
    try {
      const updated = await upload(file, docTypeCode);
      setFile(null);
      if (updated?.warning) setWarning(updated.warning);
    } catch (ex) {
      setErr(apiErrorMessage(ex, t("No se pudo cargar el CAF.")));
    }
  };

  return (
    <StepLayout
      title={t("Folios (CAF)")}
      description={t("Sube el CAF que el SII te autoriza por cada tipo de documento. El XML se guarda cifrado.")}
    >
      <InfoNote tone="info">
        <Trans
          i18nKey="El CAF se descarga en el SII (<em>Solicitud de Timbraje Electrónico de Documentos</em>). Usa folios de <strong>Maullín</strong> para pruebas y de <strong>Palena</strong> para producción."
          components={{ em: <em />, strong: <strong /> }}
        />
      </InfoNote>

      {cafs.length > 0 && (
        <div className="flex flex-col gap-1.5">
          {cafs.map((c) => (
            <div
              key={c.docTypeCode}
              className="flex items-center justify-between rounded-lg border border-border px-3 py-2 text-sm"
            >
              <span className="font-medium text-fg">
                {t(CAF_DOC_TYPES.find((d) => d.value === c.docTypeCode)?.label ?? `Tipo ${c.docTypeCode}`)}
              </span>
              <span className="text-fg-muted">
                {t("Folios {{from}}–{{to}}", { from: c.rangeFrom, to: c.rangeTo })}
              </span>
            </div>
          ))}
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field label={t("Tipo de documento del CAF")} required>
          <Select value={docTypeCode} onChange={(ev) => setDocTypeCode(ev.target.value)} options={CAF_DOC_TYPES.map((o) => ({ ...o, label: t(o.label) }))} />
        </Field>
      </div>
      <FileDrop accept=".xml" file={file} onSelect={(f) => { setFile(f); setErr(""); }} hint={t("Archivo XML del CAF.")} />
      {err && <InfoNote tone="warning">{err}</InfoNote>}
      {warning && <InfoNote tone="warning">{warning}</InfoNote>}

      <StepFooter
        onBack={goBack}
        primaryLabel={t("Continuar")}
        onPrimary={goNext}
        primaryDisabled={cafs.length === 0}
        secondary={
          <Button variant="outline" onClick={submit} loading={saving} disabled={!file}>
            {t("Cargar CAF")}
          </Button>
        }
      />
    </StepLayout>
  );
}

// ── Paso 4b: punto de venta (Argentina) ────────────────────────────────────────
export function PointOfSaleStep({ config, goBack, goNext }) {
  const { t } = useTranslation();
  const save = useInvoicingStore((s) => s.savePointOfSale);
  const saving = useInvoicingStore((s) => s.saving);
  const [pos, setPos] = useState(config?.pointOfSale ? String(config.pointOfSale) : "");
  const [err, setErr] = useState("");

  const valid = /^\d+$/.test(pos) && Number(pos) > 0;

  const submit = async () => {
    setErr("");
    try {
      await save(Number(pos));
      goNext();
    } catch (ex) {
      setErr(apiErrorMessage(ex, t("No se pudo guardar el punto de venta.")));
    }
  };

  return (
    <StepLayout
      title={t("Punto de venta")}
      description={t("El número de Punto de Venta tipo «Web Services» que diste de alta en ARCA.")}
    >
      <InfoNote tone="info">
        <Trans
          i18nKey="Debe ser un punto de venta <strong>nuevo y exclusivo</strong> para web services, distinto del que uses por otros medios. ARCA lleva la numeración de los comprobantes por punto de venta."
          components={{ strong: <strong /> }}
        />
      </InfoNote>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field label={t("Número de punto de venta")} required>
          <Input
            type="number"
            min="1"
            value={pos}
            onChange={(ev) => setPos(ev.target.value)}
            invalid={!!pos && !valid}
            placeholder={t("Ej. 3")}
          />
        </Field>
      </div>
      {err && <InfoNote tone="warning">{err}</InfoNote>}
      <StepFooter
        onBack={goBack}
        primaryLabel={t("Guardar y continuar")}
        onPrimary={submit}
        primaryLoading={saving}
        primaryDisabled={!valid}
      />
    </StepLayout>
  );
}

// ── Paso 5: reglas de emisión ──────────────────────────────────────────────────
export function EmissionRulesStep({ config, country, goBack, goNext }) {
  const { t } = useTranslation();
  const save = useInvoicingStore((s) => s.saveEmissionRules);
  const saving = useInvoicingStore((s) => s.saving);
  const [emitTrigger, setEmitTrigger] = useState(config?.emitTrigger ?? "on_payment");
  const [defaultDocType, setDefaultDocType] = useState(config?.defaultDocType ?? "");
  const [taxRate, setTaxRate] = useState(
    config?.taxRate != null ? String(config.taxRate) : String(TAX_RATE_HINT[country] ?? ""),
  );
  const [err, setErr] = useState("");
  const [fieldErr, setFieldErr] = useState({});

  // Tax rate is optional; when present it must be a finite number within 0-100.
  const taxRateOk =
    taxRate === "" || (Number.isFinite(Number(taxRate)) && Number(taxRate) >= 0 && Number(taxRate) <= 100);
  const canSave = !!emitTrigger && !!defaultDocType && taxRateOk;

  const submit = async () => {
    setErr("");
    setFieldErr({});
    try {
      await save({
        emitTrigger,
        defaultDocType,
        taxRate: taxRate === "" ? undefined : Number(taxRate),
      });
      goNext();
    } catch (ex) {
      // A business 422 carries `field`: anchor the message to the field that caused it.
      const field = ex?.response?.data?.field;
      const message = apiErrorMessage(ex, t("No se pudo guardar."));
      if (field === "defaultDocType") {
        setFieldErr({ defaultDocType: message });
      } else {
        setErr(message);
      }
    }
  };

  return (
    <StepLayout title={t("Reglas de emisión")} description={t("Cuándo se emite la factura y qué documento se emite por defecto.")}>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field label={t("¿Cuándo emitir?")} required>
          <Select value={emitTrigger} onChange={(ev) => setEmitTrigger(ev.target.value)} options={EMIT_TRIGGER_OPTIONS.map((o) => ({ ...o, label: t(o.label) }))} />
        </Field>
        <Field
          label={t("Documento por defecto")}
          required
          error={fieldErr.defaultDocType || ""}
          help={country === "CL" ? t("Necesitas un CAF cargado de este tipo.") : t("La letra A/B/C se deriva del receptor.")}
        >
          <Select
            value={defaultDocType}
            onChange={(ev) => {
              setDefaultDocType(ev.target.value);
              setFieldErr((p) => ({ ...p, defaultDocType: "" }));
            }}
            placeholder={t("Selecciona…")}
            options={(DOC_TYPE_OPTIONS[country] ?? []).map((o) => ({ ...o, label: t(o.label) }))}
          />
        </Field>
        <Field
          label={t("Tasa de impuesto referencial (%)")}
          help={t("El monto real se toma de la nota de cobro.")}
          error={taxRateOk ? "" : t("La tasa debe estar entre 0 y 100.")}
        >
          <Input type="number" min="0" max="100" value={taxRate} onChange={(ev) => setTaxRate(ev.target.value)} />
        </Field>
      </div>
      {err && <InfoNote tone="warning">{err}</InfoNote>}
      <StepFooter
        onBack={goBack}
        primaryLabel={t("Guardar y continuar")}
        onPrimary={submit}
        primaryLoading={saving}
        primaryDisabled={!canSave}
      />
    </StepLayout>
  );
}

// ── Paso 6: prueba de conexión ─────────────────────────────────────────────────
export function ConnectionTestStep({ config, country, goBack, goNext }) {
  const { t } = useTranslation();
  const test = useInvoicingStore((s) => s.testConnection);
  const testing = useInvoicingStore((s) => s.testing);
  const e = entity(country);
  const [result, setResult] = useState(
    config?.gates?.connectionTested ? { ok: true, detail: t("Conexión verificada anteriormente.") } : null,
  );

  const run = async () => {
    try {
      const r = await test();
      setResult(r);
    } catch (ex) {
      setResult({ ok: false, detail: apiErrorMessage(ex, t("No se pudo probar la conexión.")) });
    }
  };

  return (
    <StepLayout
      title={t("Prueba de conexión")}
      description={t("Verificamos tus credenciales contra {{authName}} en {{env}}.", { authName: e.authName, env: envLabel(country, config?.environment) })}
    >
      <InfoNote tone="info">
        <Trans
          i18nKey="Esto <strong>no emite ninguna factura</strong>: solo autentica tu certificado y autorización contra {{authName}} ({{flow}})."
          values={{ authName: e.authName, flow: country === "CL" ? t("semilla → token") : t("WSAA → ticket de acceso") }}
          components={{ strong: <strong /> }}
        />
      </InfoNote>

      {result && (
        <div
          className={
            "flex items-start gap-2.5 rounded-lg px-3 py-3 text-sm " +
            (result.ok ? "bg-status-success-soft text-status-success" : "bg-status-warning-soft text-status-warning")
          }
        >
          {result.ok ? (
            <CheckCircle2 className="w-5 h-5 mt-0.5 shrink-0" aria-hidden />
          ) : (
            <XCircle className="w-5 h-5 mt-0.5 shrink-0" aria-hidden />
          )}
          <div className="leading-snug">{result.detail}</div>
        </div>
      )}

      <div>
        <Button variant="outline" onClick={run} loading={testing} iconLeft={<PlugZap size={16} />}>
          {result ? t("Probar de nuevo") : t("Probar conexión")}
        </Button>
      </div>

      <StepFooter onBack={goBack} primaryLabel={t("Continuar")} onPrimary={goNext} />
    </StepLayout>
  );
}

// ── Paso 7: declaración de certificación (solo producción) ─────────────────────
export function CertificationStep({ config, country, goBack, goNext }) {
  const { t } = useTranslation();
  const declare = useInvoicingStore((s) => s.declareCertification);
  const saving = useInvoicingStore((s) => s.saving);
  const e = entity(country);
  const [approved, setApproved] = useState(!!config?.certificationApproved);
  const [resolutionNumber, setResolutionNumber] = useState(config?.resolutionNumber ?? "");
  const [resolutionDate, setResolutionDate] = useState(
    config?.resolutionDate ? String(config.resolutionDate).slice(0, 10) : "",
  );
  const [err, setErr] = useState("");

  const needsResolution = country === "CL";
  const canSave = approved && (!needsResolution || (resolutionNumber.trim() && resolutionDate));

  const submit = async () => {
    setErr("");
    try {
      await declare({
        certificationApproved: true,
        resolutionNumber: needsResolution ? resolutionNumber.trim() : undefined,
        resolutionDate: needsResolution && resolutionDate ? new Date(resolutionDate).toISOString() : undefined,
      });
      goNext();
    } catch (ex) {
      setErr(apiErrorMessage(ex, t("No se pudo registrar la declaración.")));
    }
  };

  return (
    <StepLayout
      title={t("Certificación aprobada")}
      description={t("Declara que {{authName}} ya te autorizó a emitir en producción.", { authName: e.authName })}
    >
      <InfoNote tone="warning">
        <Trans
          i18nKey="Es una <strong>declaración bajo tu responsabilidad</strong>. Beartrack no comprueba ante {{authName}} que la {{res}} exista."
          values={{ authName: e.authName, res: country === "CL" ? t("Resolución") : t("homologación") }}
          components={{ strong: <strong /> }}
        />
      </InfoNote>
      <label className="flex items-start gap-2.5 cursor-pointer">
        <input
          type="checkbox"
          checked={approved}
          onChange={() => setApproved((v) => !v)}
          className="mt-0.5 h-4 w-4 accent-brand-500"
        />
        <span className="text-sm text-fg leading-snug">
          {country === "CL"
            ? t("Aprobé la certificación en Maullín y el SII emitió la Resolución que me autoriza.")
            : t("Completé la homologación y mi certificado y punto de venta de producción están operativos.")}
        </span>
      </label>

      {needsResolution && approved && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label={t("N° de Resolución del SII")} required>
            <Input value={resolutionNumber} onChange={(ev) => setResolutionNumber(ev.target.value)} />
          </Field>
          <Field label={t("Fecha de la Resolución")} required>
            <Input type="date" value={resolutionDate} onChange={(ev) => setResolutionDate(ev.target.value)} />
          </Field>
        </div>
      )}

      {err && <InfoNote tone="warning">{err}</InfoNote>}
      <StepFooter
        onBack={goBack}
        primaryLabel={t("Guardar y continuar")}
        onPrimary={submit}
        primaryLoading={saving}
        primaryDisabled={!canSave}
      />
    </StepLayout>
  );
}

// Etiquetas legibles de los gates que faltan para activar.
const GATE_LABEL = {
  connection_test: "Prueba de conexión exitosa",
  certificate: "Certificado digital cargado",
  certificate_expired: "Certificado vigente (el actual está vencido)",
  certification: "Certificación / homologación declarada",
  emission_rules: "Reglas de emisión definidas",
  doctype_backing: "Folios (CAF) del documento por defecto",
  point_of_sale: "Punto de venta definido",
};

// ── Paso 8: activación ─────────────────────────────────────────────────────────
export function ActivationStep({ config, country, goBack, onDone }) {
  const { t } = useTranslation();
  const activate = useInvoicingStore((s) => s.activate);
  const saving = useInvoicingStore((s) => s.saving);
  const e = entity(country);
  const [err, setErr] = useState("");

  const unmet = config?.gates?.unmet ?? [];
  const ready = unmet.length === 0;
  const isProd = config?.environment === "production";

  const checklist = [
    { key: "identity", label: "Datos fiscales", done: !!config?.taxId },
    { key: "certificate", label: "Certificado digital", done: !unmet.includes("certificate") && !unmet.includes("certificate_expired") },
    country === "CL"
      ? { key: "folios", label: "Folios (CAF)", done: !unmet.includes("doctype_backing") }
      : { key: "pos", label: "Punto de venta", done: !unmet.includes("point_of_sale") },
    { key: "connection_test", label: "Prueba de conexión", done: !unmet.includes("connection_test") },
    ...(isProd ? [{ key: "certification", label: "Certificación declarada", done: !unmet.includes("certification") }] : []),
  ];

  const submit = async () => {
    setErr("");
    try {
      await activate();
      onDone?.();
    } catch (ex) {
      const data = ex?.response?.data;
      if (data?.unmet?.length) {
        setErr(t("Faltan requisitos: {{list}}.", { list: data.unmet.map((u) => t(GATE_LABEL[u] ?? u)).join(", ") }));
      } else {
        setErr(apiErrorMessage(ex, t("No se pudo activar.")));
      }
    }
  };

  return (
    <StepLayout
      title={t("Activación")}
      description={t("Revisa que todo esté listo para emitir en {{env}}.", { env: envLabel(country, config?.environment) })}
    >
      <div className="flex flex-col gap-2">
        {checklist.map((c) => (
          <div key={c.key} className="flex items-center gap-2.5 text-sm">
            {c.done ? (
              <CheckCircle2 className="w-4 h-4 text-status-success shrink-0" aria-hidden />
            ) : (
              <XCircle className="w-4 h-4 text-status-error shrink-0" aria-hidden />
            )}
            <span className={c.done ? "text-fg" : "text-fg-muted"}>{t(c.label)}</span>
          </div>
        ))}
      </div>

      {isProd ? (
        <InfoNote tone="warning">
          <Trans
            i18nKey="Al activar, las facturas reales se emitirán ante {{authName}} con <strong>validez legal</strong>."
            values={{ authName: e.authName }}
            components={{ strong: <strong /> }}
          />
        </InfoNote>
      ) : (
        <InfoNote tone="info">
          <Trans
            i18nKey="Estás activando el ambiente de <strong>pruebas</strong> ({{env}}). No tiene validez legal."
            values={{ env: envLabel(country, config?.environment) }}
            components={{ strong: <strong /> }}
          />
        </InfoNote>
      )}

      {err && <InfoNote tone="warning">{err}</InfoNote>}
      <StepFooter
        onBack={goBack}
        primaryLabel={isProd ? t("Activar producción") : t("Activar pruebas")}
        onPrimary={submit}
        primaryLoading={saving}
        primaryDisabled={!ready}
      />
    </StepLayout>
  );
}
